import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

export interface Role {
  _id: string;
  roleId?: string;
  is_active: boolean;
  role_key: string;
  role_name: string;
  role_description?: string;
  permissions?: string[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface RoleMetrics {
  total_roles: number;
  roles_with_users: number;
  total_users_with_roles: number;
}

export interface CreateRoleData {
  role_key: string;
  role_name: string;
  role_description: string;
}

export interface UpdateRoleData {
  role_key: string;
  role_name: string;
  role_description: string;
}

export interface RoleListParams {
  skip: number;
  limit: number;
  role_key?: string;
  role_name?: string;
  role_id?: string;
}

export interface RoleListResponse {
  success: boolean;
  code: number;
  description?: string;
  data: Role[];
  metrics_data?: RoleMetrics;
}

export interface RoleStatsResponse {
  success: boolean;
  code: number;
  data: {
    total_roles: number;
    active_role: number;
    nonActive_role: number;
  };
}

class RolesService {
  async getRoles(params: RoleListParams): Promise<RoleListResponse> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ROLES.LIST, params);
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }


  async createRole(roleData: CreateRoleData): Promise<Role> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ROLES.CREATE, roleData);
      return response.data.data; // Assuming API returns {success, code, data: role}
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  async updateRole(id: string, roleData: UpdateRoleData): Promise<Role> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.ROLES.UPDATE(id), roleData);
      return response.data.data; // Assuming API returns {success, code, data: role}
    } catch (error) {
      console.error(`Error updating role ${id}:`, error);
      throw error;
    }
  }

  async deleteRole(id: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.ROLES.DELETE(id));
    } catch (error) {
      console.error(`Error deleting role ${id}:`, error);
      throw error;
    }
  }

  async getRolesDropdown(): Promise<Array<{ _id: string; role_key: string; role_name: string }>> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ROLES.DROPDOWN);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching roles dropdown:', error);
      throw error;
    }
  }

  async getRoleStats(): Promise<RoleStatsResponse> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ROLES.ROLE_REPORT);
      return response.data;
    } catch (error) {
      console.error('Error fetching role stats:', error);
      throw error;
    }
  }
}

export const rolesService = new RolesService();
export default rolesService;